import React, { useState } from 'react';
import FormControlLabel from '@mui/material/FormControlLabel';
import { Box, FormControl, IconButton, Popover, Radio, RadioGroup, Typography } from '@mui/material';
import { LockStrategy } from '../../types';
import InfoIcon from '@mui/icons-material/Info';

const LockStrategyRadio = ({
  lockStrategy,
  setLockStrategy,
}: {
  lockStrategy: LockStrategy;
  setLockStrategy: (strategy: LockStrategy) => void;
}) => {
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);

  const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setLockStrategy(Number((event.target as HTMLInputElement).value) as LockStrategy);
  };

  const handleInfoClick = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleInfoClose = () => {
    setAnchorEl(null);
  };

  const infoOpen = Boolean(anchorEl);
  const infoId = infoOpen ? 'lock-strategy-info-popover' : undefined;

  return (
    <FormControl>
      <Typography variant="body2" sx={{ mb: 1 }}>
        When this test auto-locks, when do you want students &quot;In Progress&quot; to stop?
        <IconButton
          size="small"
          onClick={handleInfoClick}
          sx={{ color: 'text.secondary' }}
          aria-describedby={infoId}
          aria-label="More information about lock strategy"
        >
          <InfoIcon fontSize="small" />
        </IconButton>
      </Typography>
      <RadioGroup
        aria-labelledby="lock-strategy-radio-group"
        name="lock-strategy-radio-group"
        value={lockStrategy}
        onChange={handleChange}
      >
        <FormControlLabel
          data-testid="lock-strategy-use-remaining-time"
          value={LockStrategy.USE_REMAINING_TIME}
          control={<Radio />}
          label={
            <Typography variant='body2'>
              Let them finish their remaining time
            </Typography>
          }
        />
        <FormControlLabel
          data-testid="lock-strategy-pause-immediately"
          value={LockStrategy.PAUSE_IMMEDIATELY}
          control={<Radio />}
          label={
            <Typography variant='body2'>
              Pause them immediately, let them continue later
            </Typography>
          }
        />
        <FormControlLabel
          data-testid="lock-strategy-pause-and-score"
          value={LockStrategy.PAUSE_AND_SCORE}
          control={<Radio />}
          label={
            <Typography variant='body2'>
              Pause them immediately and score
            </Typography>
          }
        />
      </RadioGroup>

      <Popover
        id="lock-strategy-info-popover"
        open={infoOpen}
        anchorEl={anchorEl}
        onClose={handleInfoClose}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'left',
        }}
        transformOrigin={{
          vertical: 'center',
          horizontal: 'left',
        }}
      >
        <Box sx={{ p: 2, maxWidth: 400 }}>
          <Typography variant="h6" component="p" sx={{ mb: 2 }}>
            What happens when the unlock window ends?
          </Typography>
          <Typography variant="body2" sx={{ mb: 2 }}>
            Let&apos;s say the exam duration is 30 minutes, and you set the unlock
            window to 24 hours.
          </Typography>
          <Typography variant="body2" sx={{ mb: 2 }}>
            A student starts the exam 10 minutes before the 24-hour window expires.
          </Typography>
          <Typography variant="body2" sx={{ mb: 1 }}>
            <strong>If you let them use their remaining time:</strong> they will still continue
            taking the exam uninterrupted for the remaining 20 minutes, even after the unlock window
            ends.
          </Typography>
          <Typography variant="body2" sx={{ mb: 2 }}>
            <strong>If you pause them immediately:</strong> the exam will pause
            after 10 minutes. Their progress will be saved. If you unlock the exam
            again later, they can resume with 20 minutes remaining.
          </Typography>
          <Typography variant="body2" sx={{ fontStyle: 'italic' }}>
            Students who completed and submitted the exam before the unlock window
            expires are not affected.
          </Typography>
        </Box>
      </Popover>
    </FormControl>
  );
};

export default LockStrategyRadio;